import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShieldCheck, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, eur } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";
import { StockBadge } from "../components/shop/StockBadge";
import { CardBackArt } from "../components/shop/CardBackArt";
import { LaunchSignup } from "../components/shop/Launch";
import { useStore } from "../context/StoreContext";
import { Clock } from "lucide-react";
import { ProductReviews } from "../components/community/ProductReviews";
import { LangBadge, AvailabilityLine } from "../components/shop/Flag";
import { langName, conditionLabel, contentsLabel } from "../lib/editions";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, pname, pdesc, tr, lang } = useLang();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState(false);
  const { prelaunch, free_shipping_de } = useStore();

  useEffect(() => { api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);

  if (err) return <div className="py-32 text-center text-[#707783]" data-testid="product-not-found">404 · <Link to="/shop" className="underline">{t("back_shop")}</Link></div>;
  if (!p) return <div className="min-h-[60vh]" />;
  const max = Math.min(p.stock, p.max_per_order || 5);

  return (
    <div className="bg-[#F7F5FB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="space-y-4">
          <div className="relative aspect-square rounded-[28px] overflow-hidden bg-white border border-[#120E1F]/5 group">
            {prelaunch ? <CardBackArt size="lg" label={t("coming_soon_short")} /> : <img src={imgUrl(p.images?.[img])} alt={pname(p)} className="h-full w-full object-cover" data-testid="product-main-image" />}
            <div className="holo absolute inset-0 pointer-events-none" />
          </div>
          {!prelaunch && p.images?.length > 1 && (
            <div className="flex gap-3">
              {p.images.map((src, i) => (
                <button key={src} onClick={() => setImg(i)} data-testid={`product-thumb-${i}`} className={`h-20 w-20 rounded-xl overflow-hidden border-2 ${i === img ? "border-[#F2C230]" : "border-transparent"}`}>
                  <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-6 lg:pt-6">
          <Link to={`/shop?category=${p.category}`} className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">{t("cat")[p.category]}</Link>
          <h1 className="font-display text-4xl sm:text-5xl text-[#120E1F] leading-tight" data-testid="product-title">{pname(p)}</h1>
          <div className="flex flex-wrap items-center gap-3 -mt-2"><LangBadge code={p.language} size="lg" testid="product-lang-badge" /><span className="text-sm text-[#6E3FB8]">{t("cat")[p.category]}{langName(p.language, lang) ? ` (${langName(p.language, lang)})` : ""}</span></div>
          {prelaunch ? (
            <div className="rounded-2xl bg-[#120E1F] p-6 space-y-4" data-testid="product-coming-soon">
              <p className="flex items-center gap-2 font-display text-2xl text-white"><Clock className="h-5 w-5 text-[#F2C230]" />{t("coming_soon")}</p>
              <p className="text-sm text-white/60">{t("coming_soon_text")}</p>
              <LaunchSignup testid="pdp-launch-signup" />
            </div>
          ) : (<>
          <AvailabilityLine p={p} testid="product-stock-badge" />
          <div>
            <p className="font-mono text-3xl font-semibold text-[#120E1F]" data-testid="product-price">{eur(p.price)}</p>
            <p className="text-xs text-[#707783] mt-1">{t("vat")}</p>
          </div>
          </>)}
          <p className="text-[#707783] leading-relaxed" data-testid="product-description">{pdesc(p)}</p>
          {!prelaunch && p.stock > 0 && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center bg-white border border-[#120E1F]/10 rounded-full h-12">
                <button data-testid="qty-dec-btn" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4"><Minus className="h-4 w-4" /></button>
                <span className="w-8 text-center font-mono" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-inc-btn" onClick={() => setQty(Math.min(max, qty + 1))} className="px-4"><Plus className="h-4 w-4" /></button>
              </div>
              <button data-testid="add-to-cart-btn" onClick={() => { add(p, qty); toast.success(t("added")); }} className="btn-gold flex-1 min-w-[200px] h-12">{t("add_to_cart")}</button>
              <p className="w-full text-xs text-[#707783]">{t("max_per_order", { n: p.max_per_order || 5 })}</p>
            </div>
          )}
          <dl className="rounded-2xl overflow-hidden border border-[#120E1F]/5 text-sm divide-y divide-[#120E1F]/5 bg-white" data-testid="product-specs">
            {[[tr("Sprache", "Language"), <LangBadge code={p.language} />], [tr("Marktedition", "Market edition"), p.market_region], ["Set", p.set_name + (p.set_code ? ` (${p.set_code})` : "")],
              [tr("Produkttyp", "Product type"), t("cat")[p.category]], [tr("Inhalt", "Contents"), contentsLabel(p, lang)], [tr("Zustand", "Condition"), conditionLabel(p, lang)],
              [tr("Versand aus", "Ships from"), p.shipping_origin === "DE" ? tr("Deutschland", "Germany") : p.shipping_origin], ["SKU", p.sku], ["EAN", p.ean]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[140px_1fr] px-4 py-3" data-testid={`spec-${k}`}><dt className="text-[#707783]">{k}</dt><dd className="font-medium text-[#120E1F]">{v}</dd></div>
            ))}
          </dl>
          <div className="space-y-3 text-sm text-[#120E1F]">
            <p className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[#F2C230]" />{t("v1d")}</p>
            <p className="flex items-center gap-3"><PackageCheck className="h-4 w-4 text-[#F2C230]" />{t("v2d")}</p>
            <p className="flex items-center gap-3"><Truck className="h-4 w-4 text-[#F2C230]" />{t("free_ship_ok")} (≥ {free_shipping_de} €)</p>
          </div>
        </div>
      </div>
      <ProductReviews productId={p.id} />
    </div>
  );
}
